import { useContext, useState } from "react";
import Swal from "sweetalert2";

// react-router-dom components
import { Link } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import Grid from "@mui/material/Grid";
import MuiLink from "@mui/material/Link";

// @mui icons
import FacebookIcon from "@mui/icons-material/Facebook";
import GitHubIcon from "@mui/icons-material/GitHub";
import GoogleIcon from "@mui/icons-material/Google";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Authentication layout components
import BasicLayoutLanding from "layouts/authentication/components/BasicLayoutLanding";

// Images
import brandWhite from "assets/images/logo-white.png";

import AuthService from "services/auth-service";
import { getDashboardAccessError } from "auth/access";
import { AuthContext, useMaterialUIController } from "context";

function Login() {
  const authContext = useContext(AuthContext);

  const [user, setUser] = useState({});
  const [rememberMe, setRememberMe] = useState(false);

  const [inputs, setInputs] = useState({
    email: "",
    password:""
  });

  const [errors, setErrors] = useState({
    emailError: false,
    passwordError: false,
  });

  const [controller] = useMaterialUIController();
  const { darkMode, sidenavColor } = controller;

  const addUserHandler = (newUser) => setUser(newUser);

  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  const changeHandler = (e) => {
    setInputs({
      ...inputs,
      [e.target.name]: e.target.value,
    });
  };

  const submitHandler = async (e) => {
    // check rememeber me
    e.preventDefault();

    const mailFormat = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

    if (inputs.email.trim().length === 0 || !inputs.email.trim().match(mailFormat)) {
      setErrors({ ...errors, emailError: true });
      return;
    }

    if (inputs.password.trim().length < 6) {
      setErrors({ ...errors, passwordError: true });
      return;
    }

    const newUser = { email: inputs.email, password: inputs.password };
    addUserHandler(newUser);

    const myData = {
      data: {
        type: "token",
        attributes: { ...newUser },
      },
    };

    try {
      const response = await AuthService.login(myData);

      if (response.access_token) {
        localStorage.setItem("token", response.access_token);
      }

      // 1. & 2. Verificar Role e bloqueio da conta antes de liberar o acesso
      try {
        const userProfile = await AuthService.getProfile();
        
        // Só DJ entra (cliente e superadmin não) e conta bloqueada não entra
        const accessError = getDashboardAccessError(userProfile);
        if (accessError) {
          throw { message: accessError, denied: true };
        }

      } catch (validationError) {
        // Acesso negado: invalida o token recém-criado na API e limpa o cookie compartilhado
        // com o Site (o AuthService.login já o gravou), senão a sessão continuaria lá.
        if (validationError?.denied) await AuthService.logout();
        localStorage.removeItem("token");
        throw validationError;
      }

      authContext.login(response.access_token, response.refresh_token);
    } catch (res) {
      const errorMessage = res.hasOwnProperty("message") ? res.message : (res.errors ? res.errors[0].detail : "Erro desconhecido");
      Swal.fire({
        icon: "error",
        title: "Erro de Autenticação",
        text: errorMessage,
      });
    }
  };

  return (
    <BasicLayoutLanding>
      <Card>
        <MDBox
          variant="gradient"
          bgColor={sidenavColor}
          borderRadius="lg"
          coloredShadow={sidenavColor}
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDBox component="img" src={brandWhite} alt="Logo" width="10rem" mt={1} />
          <Grid container spacing={3} justifyContent="center" sx={{ mt: 1, mb: 2 }}>
            
          </Grid>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form" method="POST" onSubmit={submitHandler}>
            <MDBox mb={2}>
              <MDInput
                type="email"
                label="Email"
                fullWidth
                name="email"
                value={inputs.email}
                onChange={changeHandler}
                error={errors.emailError}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="password"
                label="Senha"
                fullWidth
                name="password"
                value={inputs.password}
                onChange={changeHandler}
                error={errors.passwordError}
              />
            </MDBox>
            <MDBox display="flex" alignItems="center" ml={-1}>
              <Switch checked={rememberMe} onChange={handleSetRememberMe} />
              <MDTypography
                variant="button"
                fontWeight="regular"
                color="text"
                onClick={handleSetRememberMe}
                sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
              >
                &nbsp;&nbsp;Lembrar-me
              </MDTypography>
            </MDBox>
            <MDBox mt={4} mb={1}>
              <MDButton variant="gradient" color={sidenavColor} fullWidth type="submit">
                Entrar
              </MDButton>
            </MDBox>
            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                Esqueceu sua senha? Redefina{" "}
                <MDTypography
                  component={Link}
                  to="/auth/forgot-password"
                  variant="button"
                  color={sidenavColor}
                  fontWeight="medium"
                  textGradient
                >
                  aqui
                </MDTypography>
              </MDTypography>
            </MDBox>
            <MDBox mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                Não tem uma conta?{" "}
                <MDTypography
                  component={Link}
                  to="/auth/register"
                  variant="button"
                  color={sidenavColor}
                  fontWeight="medium"
                  textGradient
                >
                  Cadastre-se
                </MDTypography>
              </MDTypography>
            </MDBox>
            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography variant="caption" color="text">
                Ao continuar, você concorda com nossos{" "}
                <a href="https://djparafestas.com.br/termos-uso" target="_blank" style={{ color: "inherit", textDecoration: "underline" }}>
                  Termos de Uso
                </a>
                {" "}e{" "}
                <a href="https://djparafestas.com.br/politica-de-privacidade" target="_blank" style={{ color: "inherit", textDecoration: "underline" }}>
                  Política de Privacidade
                </a>.
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayoutLanding>
  );
}

export default Login;
